/**
 * @fileoverview 基础数据管理类模块
 *
 * 该模块提供了基础的业务数据管理功能，包括表单数据管理、字典数据管理、API 请求管理等核心功能。
 * 支持数据绑定、路由参数处理、事件监听等特性，是整个数据层的核心基础类。
 *
 * @author 开发团队
 * @since 1.0.0
 * @version 1.0.0
 * @category Store
 * @group Core
 */
import { createForm, Form, FormPathPattern, IFormMergeStrategy, IFormProps } from '@formily/core';
import { action, define, observable } from '@formily/reactive';
import { addWithLimit, DeepPartial, IAny, IAnyObject, IObjKey, isSuccess, isVacuous, mergeWithArrayOverride } from '@yimoka/shared';
import { cloneDeep, get, omit, omitBy, pick, pickBy, PropertyPath, set } from 'lodash-es';

import { handleAfterAtFetch, IAfterAtFetch } from './aop';
import { IStoreAPI, IStoreHTTPRequest, IStoreResponse, runAPI } from './api';
import { IStoreDict, IStoreDictConfig, IStoreDictLoading } from './dict';
import { IField, IFieldsConfig, parseSearchParam, valueToSearchParam } from './field';
import { INotifier } from './notifier';

import type { IStore } from '.';

const DF_OPTIONS: IBaseStoreOptions = {
  filterBlankAtRun: false,
  omitAtRun: [] as string[],
  bindRoute: false,
  updateRouteType: 'push',
  routeTrigger: 'unequal',
  runNow: 'never',
  urlWithDefaultFields: [] as string[],
  keys: {},
};

// 事件名称
export const EVENT_NAMES = {
  fetchSuccess: 'fetchSuccess',
  fetchError: 'fetchError',
};

/**
 * 基础业务数据管理类。
 *
 * @template V - 值的类型。
 * @template R - 响应的类型。
 * @example
 * ```ts
 * import { BaseStore } from '@yimoka/store';
 *
 * const store = new BaseStore({
 *  defaultValues: {
 *   name: '张三',
 *   age: 18,
 * }
 * });
 * ```
 */
export class BaseStore<V extends object = IAnyObject, R = IAny> {
  options: IBaseStoreOptions = cloneDeep(DF_OPTIONS);

  /**
   * 字段配置对象，用于配置字段的 schema。
   * @type {IFieldsConfig<V>}
   * @default {}
   * fieldsConfig 是一个 IFieldsConfig 类型的对象，用于配置字段。默认值是一个空对象。
   */
  fieldsConfig: IFieldsConfig = Object({});

  /**
   * 表单对象，包含表单的所有字段和方法。来源于 `@formily/core` 的 createForm 方法。
   * @type {Form<V & IAnyObject>}
   */
  form: Form<(V & IAnyObject) | IAnyObject>;

  /**
   * dictConfig 数据字典的配置,按字段配置，支持直接写数据或者配置 API 请求。
   * @type {IStoreDictConfig<V>}
   */
  dictConfig: IStoreDictConfig<V> = [];
  /**
   * 字典请求的加载状态。
   * @type {IStoreDictLoading<V>}
   */
  dictLoading: IStoreDictLoading<V> = {};

  /**
   * 字典数据 用于存储 dictConfig 中配置生成的数据。主要用于下拉框 tag 等数据源。
   * @type {IStoreDict<V>}
   */
  dict: IStoreDict<V> = {};

  /**
   * 扩展数据 用于存储一些额外的数据 可以通过 setExtInfo 方法设置
   * @type {IAnyObject}
   */
  extInfo: IAnyObject = {};

  /**
   * 请求执行器 用于执行请求
   * @type {IStoreHTTPRequest<R, V | IAnyObject>}
  */
  apiExecutor?: IStoreHTTPRequest<R, V | IAnyObject>;

  /**
   * 请求 API 的配置 支持 函数或者配置对象（参考 axios 请求配置）
   * @type {IStoreAPI<V, R>}
   * @example
   * ```ts
   * import { BaseStore } from '@yimoka/store';
   * const store = new BaseStore({
   * api: {url: '/api/user', method: 'GET'}
   * });
   * ```
   * @example
   * ```ts
   * import { BaseStore } from '@yimoka/store';
   * const store = new BaseStore({
   * api: (params) => fetch('/api/user', {method: 'GET', body: JSON.stringify(params)})
   * });
   * ```
   *
  */
  api?: IStoreAPI<V, R>;
  /**
   * 请求加载状态
   * @type {boolean}
   */
  loading = false;
  /**
   * 请求响应数据
   * @type {IStoreResponse<R, V>}
   */
  response: IStoreResponse<R, V> = {};


  /**
  * 通知器 用于通知消息的方法
  * @type {INotifier}
  */
  notifier?: INotifier;

  /**
   * 请求后的操作配置
   * @type {IAfterAtFetch}
   */
  afterAtFetch: IAfterAtFetch = {};

  /**
   * 事件监听器。
   * @private
   */
  private eventListeners: Record<string, Array<(...args: IAny[]) => void>> = {};
  /**
   * 字典请求时序 ID
   */
  private dictFetchIDMap: Record<IObjKey, number> = {};

  /**
   * 最后一次请求的 ID，请求时序保护。
   * @private
   */
  private lastFetchID = 0;
  /**
   * 请求控制器 用于取消请求
   * @type {AbortController}
   * @private
  */
  private apiController: AbortController | undefined;
  /**
   * 选项对象，包含默认选项。
   * @type {IBaseStoreOptions}
   */

  /**
   * 创建 BaseStore 实例
   *
   * @summary 初始化基础数据管理类，配置表单、字典、API 等核心功能
   * @param config - 配置对象，包含默认值、字典配置、API 配置等
   * @param config.defaultValues - 表单的默认值
   * @param config.dictConfig - 数据字典配置
   * @param config.apiExecutor - API 请求执行器
   * @param config.api - API 配置
   * @param config.options - 选项配置
   * @param config.extInfo - 扩展信息
   * @param config.formConfig - 表单配置
   * @param config.fieldsConfig - 字段配置
   * @param config.defineConfig - 响应式定义配置
   * @param config.notifier - 通知器
   * @param config.afterAtFetch - 请求后处理配置
   *
   * @example
   * ```ts
   * // 创建基础数据管理实例
   * const store = new BaseStore({
   *   defaultValues: {
   *     name: '张三',
   *     age: 18,
   *     email: 'zhangsan@example.com'
   *   },
   *   api: {
   *     url: '/api/user',
   *     method: 'GET'
   *   },
   *   options: {
   *     runNow: 'always',
   *     filterBlankAtRun: true
   *   }
   * });
   * ```
   *
   * @example
   * ```ts
   * // 使用函数式 API 配置
   * const store = new BaseStore({
   *   defaultValues: { keyword: '' },
   *   api: async (params) => {
   *     const response = await fetch('/api/search', {
   *       method: 'POST',
   *       body: JSON.stringify(params)
   *     });
   *     return response.json();
   *   }
   * });
   * ```
   */
  constructor(config: IBaseStoreConfig<V, R> = {}) {
    const { defaultValues = {}, dictConfig, apiExecutor, api, options, extInfo, formConfig, fieldsConfig, defineConfig, notifier, afterAtFetch } = config;
    if (fieldsConfig) {
      this.fieldsConfig = fieldsConfig;
    }
    this.dictConfig = dictConfig || [];
    this.apiExecutor = apiExecutor;
    this.api = api;
    this.extInfo = extInfo || {};
    this.options = mergeWithArrayOverride(this.options, options);
    this.form = createForm({ ...formConfig, initialValues: defaultValues });
    this.notifier = notifier;
    if (afterAtFetch) {
      this.afterAtFetch = afterAtFetch;
    }

    define(this, {
      dict: observable,
      dictConfig: observable, // 以支持设置和重置时及时更新
      dictLoading: observable,
      response: this.options.responseShallow ? observable.shallow : observable,
      loading: observable,
      extInfo: observable,

      setDict: action,
      setFieldDictLoading: action,
      setFieldDict: action,
      setLoading: action,
      setResponse: action,
      setExtInfo: action,
      fetch: action,
      ...defineConfig,
    });

    const { runNow, bindRoute } = this.options;
    // 绑定路由时，会使用组件在进行页面时进行参数赋值，再进行请求。
    if (runNow === 'always' && !bindRoute) {
      this.fetch();
    }
  }

  get defaultValues() {
    return this.form.initialValues;
  }

  /**
   * 当前表单的值
   *
   * @type {V & IAnyObject}
   */
  get values() {
    return this.form.values;
  }

  /**
   * 获取默认值的深拷贝
   *
   * @summary 返回表单默认值的深度拷贝，避免直接修改原始默认值
   * @returns 包含默认值的深拷贝对象
   *
   * @example
   * ```ts
   * const store = new BaseStore({
   *   defaultValues: { name: '张三', info: { age: 18 } }
   * });
   * const defaults = store.getDefaultValues();
   * defaults.info.age = 20; // 不会影响原始默认值
   * console.log(store.defaultValues.info.age); // 仍然是 18
   * ```
   */
  getDefaultValuesCloneDeep = () => cloneDeep(this.defaultValues);

  /**
   * 设置表单默认值
   *
   * @summary 更新表单的初始默认值
   * @param values - 要设置的默认值（部分或全部），支持深层可选属性
   * @param strategy - 合并策略，默认为 'merge'
   *
   * @example
   * ```ts
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18 } });
   * store.setDefaultValues({ name: '李四' }); // 只更新 name，age 保持不变
   * ```
   *
   * @example
   * ```ts
   * // 深层对象的部分更新
   * const store = new BaseStore({
   *   defaultValues: {
   *     user: {
   *       profile: { name: '张三', age: 18 },
   *       settings: { theme: 'light' }
   *     }
   *   }
   * });
   * store.setDefaultValues({
   *   user: {
   *     profile: { name: '李四' } // 只更新 profile.name，其他属性保持不变
   *   }
   * });
   * ```
   */
  setDefaultValues = (values: DeepPartial<V>, strategy?: IFormMergeStrategy) => {
    this.form.setInitialValues(values, strategy);
  };

  /**
   * 设置指定路径的默认值
   *
   * @summary 通过路径设置表单默认值中的特定字段
   * @param path - 字段路径，支持点符号和数组索引
   * @param value - 要设置的值
   *
   * @example
   * ```ts
   * const store = new BaseStore({
   *   defaultValues: { user: { profile: { name: '张三' } } }
   * });
   * store.setDefaultValuesIn('user.profile.name', '李四');
   * ```
   */
  setDefaultValuesIn = (path: FormPathPattern, value: IAny) => {
    this.form.setInitialValuesIn(path, value);
  };

  /**
   * 设置表单的值。
   *
   * @param values - 部分或全部的表单值，支持深层可选属性。
   * @param strategy - （可选）合并策略，指定如何将新值与现有值合并。 'overwrite' | 'merge' | 'shallowMerge'; 默认为 'merge'。
   *
   * @example
   * // 示例1：设置表单的值 默认策略 merge
   * ```ts
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18 } });
   * store.setValues({ name: '张三' });
   * console.log(store.values); // { name: '张三', age: 18 }
   * ```
   * @example
   * // 示例2：设置表单值 overwrite
   * ```ts
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18 } });
   * store.setValues({ name: '张三' }, 'overwrite');
   * console.log(store.values); // { name: '张三' }
   * ```
   * @example
   * // 示例3：设置表单值 shallowMerge
   * ```ts
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18, info: { address: '北京' } } });
   * store.setValues({ name: '李四', info: { phone: '123' } }, 'shallowMerge');
   * console.log(store.values); // { name: '李四', age: 18, info: { phone: '123' } }
   * ```
   */
  setValues = (values: DeepPartial<V>, strategy?: IFormMergeStrategy) => {
    this.form.setValues(values, strategy);
  };

  /**
   * 设置指定字段的值。
   *
   * @param field 要设置值的字段。
   * @param value 要设置的值。
   *
   * @example
   * 示例1：设置指定字段的值。
  * ```ts
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18 } });
   * store.setFieldValue("name", '李四');
   * console.log(store.values); // { name: '李四', age: 18 }
   * ```
   * @example
   * ```ts
   * 示例2：设置指定字段的值多级字段。
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18, info: { address: '北京' } } });
   * store.setFieldValue("info.address", '上海');
   * console.log(store.values); // { name: '张三', age: 18, info: { address: '上海' } }
   * ```
   * @example
   * 示例3：设置指定字段的值多级数组字段。
   * ```ts
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18, tags: ['a', 'b'] } });
   * store.setFieldValue("tags[0]", 'c');
   * console.log(store.values); // { name: '张三', age: 18, tags: ['c', 'b'] }
   * ```
   */
  setFieldValue = (field: IField<V> | IObjKey, value: IAny) => {
    this.form.setValuesIn(field as IAny, value);
  };

  /**
   * 带参数拉取数据：先设置值再 fetch。
   *
   * @summary 原子操作，避免分散调用 setValues + fetch 导致的遗漏或顺序错误
   * @param values - 部分或全部的表单值，支持深层可选属性
   * @param strategy - （可选）合并策略，同 setValues
   * @returns Promise<IStoreResponse<R, V> | undefined> 请求响应结果
   *
   * @remarks
   * - 适用于筛选条件变更、分页切换等「改值即拉取」的场景
   * - 语义：用这些值去拉取（fetch with these values）
   *
   * @example
   * ```ts
   * await store.fetchWith({ keyword: '手机', page: 1 });
   * await store.fetchWith({ classID: selectedClassID });
   * ```
   */
  fetchWith = async (values: DeepPartial<V>, strategy?: IFormMergeStrategy) => {
    this.setValues(values, strategy);
    return this.fetch();
  };

  /**
   * 带单字段参数拉取数据：先设置字段值再 fetch。
   *
   * @summary 原子操作，适用于分页、单选等单字段变更场景
   * @param field - 要设置值的字段，支持 path 如 'info.address'
   * @param value - 要设置的值
   * @returns Promise<IStoreResponse<R, V> | undefined> 请求响应结果
   *
   * @example
   * ```ts
   * await store.fetchWithField('page', 1);
   * await store.fetchWithField('semesterCode', selectedSemesterID);
   * ```
   */
  fetchWithField = async (field: IField<V> | IObjKey, value: IAny) => {
    this.setFieldValue(field, value);
    return this.fetch();
  };

  /**
   * 根据路由中的查询参数和路径参数设置值。
   *
   * @param search - 查询参数，可以是查询字符串或对象。
   * @param params - 路径参数对象，可选。
   * @param resetMissingValues - 是否重置缺失的值，默认为 true。
  * @example
  * // 示例1：从路由查询参数设置值
  * ```ts
  * const store = new BaseStore({ defaultValues: { name: '张三', age: 18 } });
  * store.setValuesFromRoute('?name=李四&age=20');
  * console.log(store.values); // { name: '李四', age: 20 }
  * ```
  * @example
  * // 示例2：从路由查询参数和路径参数设置值
  * ```ts
  * const store = new BaseStore({ defaultValues: { name: '张三', age: 18 } });
  * store.setValuesFromRoute('?name=李四', { age: 20 });
  * console.log(store.values); // { name: '李四', age: 20 }
  * ```
  * @example
  * // 示例3：从对象查询参数设置值 不重置缺失的值
  * ```ts
  * const store = new BaseStore({ defaultValues: { name: '张三', age: 18 } });
  * store.setValuesFromRoute({ name: '李四', age: 20 });
  * console.log(store.values); // { name: '李四', age: 20 }
  * // 不重置缺失的值
  * store.setValuesFromRoute({ name: '李四' }, {}, false);
  * console.log(store.values); // { name: '李四', age: 20 }
  * // 重置缺失的值
  * store.setValuesFromRoute({ name: '李四' }, {}, true);
  * console.log(store.values); // { name: '李四', age: 18 }
  * ```
   */
  setValuesFromRoute = (
    search?: string | IAnyObject,
    params?: IAnyObject,
    resetMissingValues = true,
  ) => {
    let newValues: IAnyObject = {};
    const keys = Object.keys(this.values);
    if (typeof search === 'string' && search) {
      try {
        // 小程序会报错
        const searchParams = new URLSearchParams(search);
        keys.forEach((key) => {
          const strValue = searchParams.get(key);
          if (strValue !== null) {
            newValues[key] = parseSearchParam(
              strValue,
              this.fieldsConfig[key],
              this.defaultValues[key],
            );
          }
        });
      } catch (error) {
        console.error('setValuesFromRoute error:', error);
      }
    }
    if (typeof search === 'object') {
      Object.entries(pick(search, keys)).forEach(([key, value]) => {
        newValues[key] = parseSearchParam(
          value,
          this.fieldsConfig[key],
          this.defaultValues[key],
        );
      });
    }
    if (typeof params === 'object') {
      Object.entries(pick(params, keys)).forEach(([key, value]) => {
        newValues[key] = parseSearchParam(
          value,
          this.fieldsConfig[key],
          this.defaultValues[key],
        );
      });
    }
    if (resetMissingValues) {
      // 使用 cloneDeep 避免在多级对象修改默认值对象
      newValues = { ...this.getDefaultValuesCloneDeep(), ...newValues };
    }
    this.setValues(newValues);
  };

  /**
   * 带路由参数拉取：从路由设置值并 fetch。
   *
   * @summary 原子操作：先 setValuesFromRoute 再 fetch
   * @param search - 查询参数，可以是查询字符串或对象
   * @param params - 路径参数对象，可选
   * @param resetMissingValues - 是否重置缺失的值，默认为 true
   * @returns Promise<IStoreResponse<R, V> | undefined> 请求响应结果
   *
   * @example
   * ```ts
   * await store.fetchWithRoute(search, params);
   * await store.fetchWithRoute('?name=李四&age=20');
   * ```
   */
  fetchWithRoute = async (
    search?: string | IAnyObject,
    params?: IAnyObject,
    resetMissingValues = true,
  ) => {
    this.setValuesFromRoute(search, params, resetMissingValues);
    return this.fetch();
  };

  /**
   * 重置表单的值为默认值
   *
   * @summary 将表单所有字段重置为初始默认值
   *
   * @example
   * ```ts
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18 } });
   * store.setValues({ name: '李四', age: 20 });
   * store.resetValues();
   * console.log(store.values); // { name: '张三', age: 18 }
   * ```
  */
  resetValues = () => {
    // 使用 cloneDeep 避免在多级对象修改默认值对象
    this.form.setValues(this.getDefaultValuesCloneDeep(), 'overwrite');
  };

  /**
   * 重置指定字段的值为默认值
   *
   * @summary 将指定的一个或多个字段重置为其初始默认值
   * @param fields - 要重置的字段，支持单个字段或多字段数组
   *
   * @remarks 如果重置的字段在默认值中不存在，则不会进行重置操作
   *
   * @example
   * ```ts
   * const store = new BaseStore({ defaultValues: { name: '张三', age: 18, email: 'test@example.com' } });
   * store.setValues({ name: '李四', age: 20, email: 'new@example.com' });
   *
   * // 重置单个字段
   * store.resetFieldsValue('name');
   * console.log(store.values.name); // '张三'
   *
   * // 重置多个字段
   * store.resetFieldsValue(['age', 'email']);
   * console.log(store.values); // { name: '李四', age: 18, email: 'test@example.com' }
   * ```
   *
   */
  resetFieldsValue = (fields: Array<IField<V> | IObjKey> | IField<V> | IObjKey) => {
    this.setValues(pick(this.getDefaultValuesCloneDeep(), fields));
  };

  /**
   * 带重置拉取：用重置为默认值后的数据去拉取。
   *
   * @summary 原子操作：先 resetValues 再 fetch
   * @returns Promise<IStoreResponse<R, V> | undefined> 请求响应结果
   *
   * @example
   * ```ts
   * await store.fetchWithReset();
   * ```
   */
  fetchWithReset = async () => {
    this.resetValues();
    return this.fetch();
  };

  /**
   * 带指定字段重置拉取：用重置指定字段为默认值后的数据去拉取。
   *
   * @summary 原子操作：先 resetFieldsValue 再 fetch
   * @param fields - 要重置的字段，支持单个字段或多字段数组
   * @returns Promise<IStoreResponse<R, V> | undefined> 请求响应结果
   *
   * @example
   * ```ts
   * await store.fetchWithResetFields('keyword');
   * await store.fetchWithResetFields(['keyword', 'page']);
   * ```
   */
  fetchWithResetFields = async (fields: Array<IField<V> | IObjKey> | IField<V> | IObjKey) => {
    this.resetFieldsValue(fields);
    return this.fetch();
  };

  /**
   * 根据表单的值生成 URL search 字符串
   *
   * @summary 将当前表单值转换为 URL 查询参数字符串
   * @returns URL 查询参数字符串，不包含问号前缀
   *
   * @remarks
   * - 只有与默认值不同的字段会被包含在查询字符串中
   * - 如果字段在 options.urlWithDefaultFields 中配置，即使值与默认值相同也会被包含
   * - 空值（null、undefined、空字符串等）通常会被过滤，除非默认值也是空值
   *
   * @example
   * ```ts
   * const store = new BaseStore({
   *   defaultValues: { name: '', age: 18, city: '北京' },
   *   options: { urlWithDefaultFields: ['city'] }
   * });
   * store.setValues({ name: '张三', age: 20, city: '北京' });
   * console.log(store.genURLSearch()); // "name=%E5%BC%A0%E4%B8%89&age=20&city=%E5%8C%97%E4%BA%AC"
   * ```
   *
   * @throws 在不支持 URLSearchParams 的环境中可能抛出异常，此时返回空字符串
   */
  genURLSearch = () => {
    try {
      const searchParams = new URLSearchParams();
      Object.entries(this.values).forEach(([key, value]) => {
        const defaultValue = this.defaultValues[key];
        if ((value !== defaultValue || this.options.urlWithDefaultFields?.includes(key)) && (!isVacuous(value) || !isVacuous(defaultValue))) {
          const str = valueToSearchParam(value);
          searchParams.append(key, str);
        }
      });
      return searchParams.toString();
    } catch (error) {
      console.error('getURLSearch error:', error);
      return '';
    }
  };

  /**
   * 增加字典请求时序 ID
   *
   * @summary 为指定字段生成新的字典请求 ID，用于请求时序控制
   * @param field - 字段名
   * @returns 新生成的请求 ID
   *
   * @remarks 用于防止字典请求的竞态条件，确保只有最新的请求结果被使用
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * const requestId = store.incrDictFetchID('category');
   * console.log(requestId); // 1
   * ```
   */
  incrDictFetchID = (field: IField<V> | IObjKey) => {
    const fetchID = addWithLimit(this.dictFetchIDMap[field] ?? 0);
    this.dictFetchIDMap[field] = fetchID;
    return fetchID;
  };

  /**
   * 获取字典请求时序 ID
   *
   * @summary 获取指定字段当前的字典请求 ID
   * @param field - 字段名
   * @returns 当前请求 ID，如果未初始化则返回 0
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * console.log(store.getDictFetchID('category')); // 0
   * store.incrDictFetchID('category');
   * console.log(store.getDictFetchID('category')); // 1
   * ```
   */
  getDictFetchID = (field: IField<V> | IObjKey) => this.dictFetchIDMap[field] ?? 0;

  /**
   * 设置字典数据
   *
   * @summary 批量设置所有字段的字典数据
   * @param dict - 字典数据对象，键为字段名，值为对应的字典数据
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.setDict({
   *   category: [{ label: '电子产品', value: 'electronics' }],
   *   status: [{ label: '启用', value: 'active' }, { label: '禁用', value: 'inactive' }]
   * });
   * ```
   */
  setDict = (dict: IStoreDict<V>) => this.dict = dict;

  /**
   * 设置字段字典数据
   *
   * @summary 设置指定字段的字典数据
   * @param field - 字段名
   * @param value - 字典数据，通常是选项数组
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.setFieldDict('category', [
   *   { label: '数码产品', value: 'digital' },
   *   { label: '家电产品', value: 'appliance' }
   * ]);
   * ```
   */
  setFieldDict = (field: IField<V> | IObjKey, value: IAny) => set(this.dict, field, value);

  /**
   * 设置字段字典加载状态
   *
   * @summary 设置指定字段字典数据的加载状态
   * @param field - 字段名
   * @param bool - 加载状态，true 表示正在加载，false 表示加载完成
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.setFieldDictLoading('category', true); // 开始加载
   * // ... 执行字典数据请求
   * store.setFieldDictLoading('category', false); // 加载完成
   * ```
   */
  setFieldDictLoading = (field: IField<V> | IObjKey, bool: boolean) => this.dictLoading[field] = bool;

  /**
   * 设置 fetch 的加载状态
   *
   * @summary 更新 API 请求的加载状态
   * @param loading - 加载状态，true 表示正在请求，false 表示请求完成
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.setLoading(true); // 开始请求
   * // ... 执行 API 请求
   * store.setLoading(false); // 请求完成
   * ```
   */
  setLoading = (loading: boolean) => this.loading = loading;

  /**
   * 设置 fetch 的响应数据
   *
   * @summary 更新 API 请求的响应结果
   * @param data - 响应数据对象
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.setResponse({
   *   data: { users: [] },
   *   success: true,
   *   message: '请求成功'
   * });
   * ```
   */
  setResponse = (data: IStoreResponse<R, V>) => {
    this.response = data;
  };

  /**
   * 设置扩展数据
   *
   * @summary 在指定路径设置扩展信息数据
   * @param key - 数据路径，支持点符号和数组索引
   * @param value - 要设置的值
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.setExtInfo('user.permissions', ['read', 'write']);
   * store.setExtInfo('config.theme', 'dark');
   * ```
   */
  setExtInfo = (key: PropertyPath, value: IAny) => set(this.extInfo, key, value);

  /**
   * 获取扩展数据
   *
   * @summary 从指定路径获取扩展信息数据
   * @param key - 数据路径，支持点符号和数组索引
   * @returns 指定路径的数据值
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.setExtInfo('user.permissions', ['read', 'write']);
   * const permissions = store.getExtInfo('user.permissions');
   * console.log(permissions); // ['read', 'write']
   * ```
   */
  getExtInfo = (key: PropertyPath) => get(this.extInfo, key);

  /**
   * 执行 API 请求
   *
   * @summary 发起异步 API 请求，支持请求时序控制和参数过滤
   * @returns Promise<IStoreResponse<R, V> | undefined> 请求响应结果，可能为 undefined（当请求被取消或不是最新请求时）
   *
   * @remarks
   * - 自动管理加载状态（loading）
   * - 支持请求时序控制，确保只处理最新请求的响应
   * - 根据配置过滤空值参数
   * - 自动触发成功/失败事件
   * - 执行后置处理逻辑（afterAtFetch）
   *
   * @example
   * ```ts
   * const store = new BaseStore({
   *   api: { url: '/api/users', method: 'GET' },
   *   defaultValues: { page: 1, pageSize: 10 }
   * });
   *
   * const response = await store.fetch();
   * if (response?.success) {
   *   console.log('请求成功', response.data);
   * }
   * ```
   *
   * @example
   * ```ts
   * // 使用参数过滤
   * const store = new BaseStore({
   *   api: { url: '/api/search', method: 'POST' },
   *   defaultValues: { keyword: '', category: '' },
   *   options: { filterBlankAtRun: true } // 过滤空值
   * });
   *
   * store.setValues({ keyword: '手机', category: '' });
   * await store.fetch(); // 只会发送 { keyword: '手机' }
   * ```
   */
  fetch = async () => {
    this.setLoading(true);
    this.lastFetchID = addWithLimit(this.lastFetchID);
    if (typeof this.api !== 'function') {
      try {
        if (this.apiController) {
          this.apiController.abort();
        }
        this.apiController = new AbortController();
      } catch (error) {
        console.error('AbortController error:', error);
      }
    } else {
      this.apiController = undefined;
    }
    const fetchID = this.lastFetchID;
    const { api } = this;
    let params: IAnyObject = {};
    const { filterBlankAtRun, omitAtRun } = this.options;
    if (filterBlankAtRun === true) {
      params = pickBy(this.values, value => (!isVacuous(value)));
    } else if (Array.isArray(filterBlankAtRun) && filterBlankAtRun.length) {
      params = omitBy(this.values, (value, key) => filterBlankAtRun.includes(key) && isVacuous(value));
    } else {
      params = this.values;
    }
    if (Array.isArray(omitAtRun) && omitAtRun.length) {
      params = omit(params, omitAtRun);
    }
    const response = await runAPI<V, R>(api, this.apiExecutor, params, this.apiController);
    if (response && fetchID === this.lastFetchID) {
      this.setResponse(response);
      this.setLoading(false);
      handleAfterAtFetch(response, this);
      if (isSuccess(response)) {
        this.emitFetchSuccess(response, this);
      } else {
        this.emitFetchError(response, this);
      }
    }
    return response;
  };

  /**
   * 订阅事件
   *
   * @summary 注册事件监听器
   * @param event - 事件名称
   * @param listener - 事件监听器函数
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.on('fetchSuccess', (response, store) => {
   *   console.log('请求成功', response.data);
   * });
   * ```
   */
  on(event: string, listener: (...args: IAny[]) => void) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(listener);
  }

  /**
   * 取消订阅事件
   *
   * @summary 移除指定的事件监听器
   * @param event - 事件名称
   * @param listener - 要移除的事件监听器函数
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * const listener = (response) => console.log(response);
   * store.on('fetchSuccess', listener);
   * store.off('fetchSuccess', listener); // 移除监听器
   * ```
   */
  off(event: string, listener: (...args: IAny[]) => void) {
    if (!this.eventListeners[event]) return;
    this.eventListeners[event] = this.eventListeners[event].filter(l => l !== listener);
  }

  /**
   * 触发事件
   *
   * @summary 触发指定事件，调用所有注册的监听器
   * @param event - 事件名称
   * @param args - 传递给事件监听器的参数
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.on('customEvent', (data) => console.log('收到数据:', data));
   * store.emit('customEvent', { message: 'Hello World' });
   * ```
   */
  emit(event: string, ...args: IAny[]) {
    if (!this.eventListeners[event]) return;
    this.eventListeners[event].forEach(listener => listener(...args));
  }

  /**
   * 监听请求成功事件
   *
   * @summary 注册 API 请求成功的事件监听器
   * @param listener - 事件监听器函数，接收响应数据和 store 实例
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.onFetchSuccess((response, store) => {
   *   console.log('请求成功:', response.data);
   *   // 可以在这里处理成功后的逻辑
   * });
   * ```
   */
  onFetchSuccess(listener: IFetchListener<V, R>) {
    this.on(EVENT_NAMES.fetchSuccess, listener);
  }
  /**
   * 取消监听请求成功事件
   *
   * @summary 移除 API 请求成功的事件监听器
   * @param listener - 要移除的事件监听器函数
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * const successListener = (response) => console.log('成功');
   * store.onFetchSuccess(successListener);
   * store.offFetchSuccess(successListener); // 移除监听器
   * ```
   */
  offFetchSuccess(listener: IFetchListener<V, R>) {
    this.off(EVENT_NAMES.fetchSuccess, listener);
  }
  /**
   * 触发请求成功事件
   *
   * @summary 触发 API 请求成功事件，通知所有监听器
   * @param response - API 响应数据
   * @param store - Store 实例
   *
   * @remarks 通常由内部 fetch 方法调用，外部一般不需要手动调用
   */
  emitFetchSuccess(response: IStoreResponse<R, V>, store: IStore<V, R>) {
    this.emit(EVENT_NAMES.fetchSuccess, response, store);
  }

  // 监听请求失败事件
  /**
   * 监听请求失败事件
   *
   * @summary 注册 API 请求失败的事件监听器
   * @param listener - 事件监听器函数，接收响应数据和 store 实例
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * store.onFetchError((response, store) => {
   *   console.error('请求失败:', response.message);
   *   // 可以在这里处理错误逻辑，如显示错误提示
   * });
   * ```
   */
  onFetchError(listener: IFetchListener<V, R>) {
    this.on(EVENT_NAMES.fetchError, listener);
  }
  /**
   * 取消监听请求失败事件
   *
   * @summary 移除 API 请求失败的事件监听器
   * @param listener - 要移除的事件监听器函数
   *
   * @example
   * ```ts
   * const store = new BaseStore();
   * const errorListener = (response) => console.error('失败');
   * store.onFetchError(errorListener);
   * store.offFetchError(errorListener); // 移除监听器
   * ```
   */
  offFetchError(listener: IFetchListener<V, R>) {
    this.off(EVENT_NAMES.fetchError, listener);
  }
  /**
   * 触发请求失败事件
   *
   * @summary 触发 API 请求失败事件，通知所有监听器
   * @param response - API 响应数据
   * @param store - Store 实例
   *
   * @remarks 通常由内部 fetch 方法调用，外部一般不需要手动调用
   */
  emitFetchError(response: IStoreResponse<R, V>, store: IStore<V, R>) {
    this.emit(EVENT_NAMES.fetchError, response, store);
  }
}


export type IStoreRunNow = 'never' | 'always' | 'whenRequired'
/**
 * Store 的默认选项。
 *
 * @property {boolean} filterBlankAtRun - 执行 API 时是否过滤参数空值。支持布尔值和字符串数组。当为字符串数组时，只过滤数组中的字段。
 * @property {string[]} omitAtRun - 执行 API 时固定排除的字段。
 * @property {boolean} bindRoute - 是否绑定路由。
 * @property {'push' | 'replace'} updateRouteType - 更新路由的方法，是 'push' 还是 'replace'。
 * @property {'unequal' | 'any'} routeTrigger - 路由变化的触发条件，是值变化 ('unequal') 还是任意变化 ('any')。
 * @property {IStoreRunNow} runNow -  进入页面时是否立即拉取数据
 * @property {string[]} urlWithDefaultFields - 字值默认值与参数值相同时默认不添加到 URL search 中。如需添加，可以在此配置。默认为空数组。
 * @property {Record<string, string>} keys - 字段键的配置，例如 'page' 和 'pageSize'，用于标准化输入和输出字段。
 * @property {boolean} responseShallow - response 是深劫持还是浅劫持 默认是深劫持
 */
export type IBaseStoreOptions = {
  filterBlankAtRun: boolean | string[];
  omitAtRun: string[];
  bindRoute: boolean;
  updateRouteType: 'push' | 'replace';
  routeTrigger: 'unequal' | 'any';
  runNow: IStoreRunNow
  urlWithDefaultFields: string[];
  keys: Record<string, string>;
  responseShallow?: boolean;
}

export interface IBaseStoreConfig<V extends object = IAnyObject, R = IAny> {
  defaultValues?: V & IAnyObject;
  fieldsConfig?: IFieldsConfig;
  dictConfig?: IStoreDictConfig<V>;
  apiExecutor?: IStoreHTTPRequest<R, V>;
  api?: IStoreAPI<V, R>;
  options?: Partial<IBaseStoreOptions>;
  extInfo?: IAnyObject;
  // 表单的配置
  formConfig?: Omit<IFormProps, 'initialValues'>;
  defineConfig?: IAnyObject
  notifier?: INotifier;
  afterAtFetch?: IAfterAtFetch
}

export type IFetchListener<V extends object = IAnyObject, R = IAny> = (response: IStoreResponse<R, V>, store: IStore<V, R>) => void
